import { isFuture, isValid, parseISO } from "date-fns";
import { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";

import {
  contactEmailSchema,
  contactIdSchema,
  refineContactOrEmail,
} from "features/results/utils/contactOrEmail";
import { isIrishCountry } from "utils/country";
import { validateEircode, validatePPSN } from "utils/validators";

// The API's column limit for the free-text patient preference.
export const PATIENT_PREFERENCE_MAX_LENGTH = 250;

export const E164_PHONE_HINT =
  "Please enter a valid phone number (choose country code and national number).";

const validatePhoneNumber = (phoneNumber) => {
  try {
    if (!phoneNumber) return true;
    if (!phoneNumber.startsWith("+")) {
      return false;
    }
    return isValidPhoneNumber(phoneNumber);
  } catch {
    return false;
  }
};

const optionalE164Phone = () =>
  z
    .string()
    .optional()
    .refine((val) => {
      if (!val || val.trim() === "") return true;
      return validatePhoneNumber(val);
    }, E164_PHONE_HINT);

const ppsnRefinement = {
  refine: (val) => {
    if (!val || val.trim() === "") return true;
    return validatePPSN(val).isValid;
  },
  refineError: (val) => {
    if (!val || val.trim() === "") return { message: "" };
    return { message: validatePPSN(val).error || "Invalid PPSN" };
  },
};

const eircodeRefinement = {
  refine: (val) => {
    if (!val || val.trim() === "") return true;
    return validateEircode(val).isValid;
  },
  refineError: (val) => {
    if (!val || val.trim() === "") return { message: "" };
    return { message: validateEircode(val).error || "Invalid Eircode" };
  },
};

// Optional, and deliberately so (KIN-1733). A patient registered at the desk or over
// the phone may not have given a date of birth yet, and blocking the record on it means
// either no record or an invented date — Dalen, on defaulting walk-ins to 01/01/2001:
// "Default values always cause pain ... best left as null - this is what they are
// invented for."
//
// Optional does not mean unchecked: a date that IS given must still be a real one and
// must not be in the future. Only the "you must supply this" rule is lifted.
//
// Nothing can be submitted to the HSE without it, which is where the pressure to record
// it now lives — see HseDetailsNotice, shown on the submitting screens rather than here.
const dateOfBirthOptional = z
  .string()
  .refine((val) => {
    if (!val || val.trim() === "") return true;

    return val.length >= 10 && isValid(parseISO(val));
  }, "Please enter a valid date of birth")
  .refine((val) => {
    if (!val || val.trim() === "") return true;

    return val.length >= 10 && !isFuture(parseISO(val));
  }, "Date of birth cannot be in the future");

const genderRequired = z
  .string()
  .min(1, "Gender is required")
  .refine((val) => val !== "Select", "Please select gender");

const mobilePhoneRequired = z
  .string()
  .min(1, "Mobile phone number is required")
  .refine((val) => {
    if (!val || val.trim() === "") return false;
    return validatePhoneNumber(val);
  }, E164_PHONE_HINT);

const addressLine1Required = z.string().min(1, "Address line 1 is required");

const baseShape = {
  title: z.string().optional(),
  firstName: z.string().min(1, "First name is required"),
  surname: z.string().min(1, "Last name is required"),
  mothersMaidenName: z.string().optional(),
  patientPreference: z
    .string()
    .max(PATIENT_PREFERENCE_MAX_LENGTH, "Patient preference must be 250 characters or fewer")
    .optional(),
  doctorDetailsId: z.union([z.string(), z.number()]).optional(),
  ethnicityCode: z.string().optional(),
  financialClass: z.string().optional(),
  nextOfKin: z.string().optional(),
  nextOfKinRelationship: z.string().optional(),
  nextOfKinPhone: z.string().optional(),
  noPpsn: z.boolean().optional(),
  died: z.boolean().optional(),
  dateOfDeath: z.string().optional(),
  causeOfDeath: z.string().optional(),
  addressLine1: z.string().optional(),
  addressLine2: z.string().optional(),
  addressLine3: z.string().optional(),
  city: z.string().optional(),
  county: z.string().optional(),
  medicalCardNumber: z.string().optional(),
  country: z.string().optional(),
  primaryGpContactId: contactIdSchema,
  primaryGpEmail: contactEmailSchema,
};

const optionalEmail = (messageWhenInvalid) =>
  z
    .string()
    .optional()
    .refine((val) => {
      if (!val || val.trim() === "") return true;
      return z.string().email().safeParse(val).success;
    }, messageWhenInvalid);

const nextOfKinPhoneRefine = (data, ctx) => {
  if (
    data.nextOfKinPhone &&
    data.nextOfKinPhone.trim() !== "" &&
    !validatePhoneNumber(data.nextOfKinPhone)
  ) {
    ctx.addIssue({
      path: ["nextOfKinPhone"],
      code: z.ZodIssueCode.custom,
      message: E164_PHONE_HINT,
    });
  }
};

const deathFieldsRefine = (data, ctx) => {
  if (data.died !== true) return;

  const raw = data.dateOfDeath;
  if (raw == null || String(raw).trim() === "") {
    ctx.addIssue({
      path: ["dateOfDeath"],
      code: z.ZodIssueCode.custom,
      message: "Date of death is required",
    });
    return;
  }

  const s = String(raw).trim();
  if (s.length < 10) {
    ctx.addIssue({
      path: ["dateOfDeath"],
      code: z.ZodIssueCode.custom,
      message: "Please enter a valid date of death",
    });
    return;
  }

  const dod = parseISO(s);
  if (!isValid(dod)) {
    ctx.addIssue({
      path: ["dateOfDeath"],
      code: z.ZodIssueCode.custom,
      message: "Please enter a valid date of death",
    });
    return;
  }

  if (isFuture(dod)) {
    ctx.addIssue({
      path: ["dateOfDeath"],
      code: z.ZodIssueCode.custom,
      message: "Date of death cannot be in the future",
    });
  }
};

const ppsnRefine = (data, ctx) => {
  if (data.noPpsn) return;
  const val = data.nationalNumber;
  if (!val || val.trim() === "") return;
  if (!ppsnRefinement.refine(val)) {
    ctx.addIssue({
      path: ["nationalNumber"],
      code: z.ZodIssueCode.custom,
      ...ppsnRefinement.refineError(val),
    });
  }
};

// A medical card number is meaningless without the class it belongs to: the
// classification drives CDM/OCF/PP eligibility and reimbursement, and PCRS runs
// the scheme check against it. Left blank, the patient sails through a whole
// review and only fails at booking. Several classes carry a card number
// (GMS / Temporary GMS / Doctor Only Visit Card / Temp DV Card), so we require an
// explicit choice rather than assuming GMS and silently mis-classifying.
const financialClassRefine = (data, ctx) => {
  const hasCardNumber = !!data.medicalCardNumber?.trim();
  const hasClassification = !!data.financialClass?.trim();

  if (hasCardNumber && !hasClassification) {
    ctx.addIssue({
      path: ["financialClass"],
      code: z.ZodIssueCode.custom,
      message:
        "Select the patient classification that the medical card number belongs to",
    });
  }
};

// County and Eircode are Irish-address rules, so they cannot live in the field shape —
// they depend on `country`, which only a cross-field refinement can see (KIN-1650).
//
// County stays mandatory for an Irish address: the HL7 address maps it to XAD.4 and a lab
// order is refused without it. Abroad it becomes a free-text region, and requiring one would
// be requiring a value the patient may not have.
//
// The Eircode format check is likewise Irish-only. Applying it to a foreign postcode rejects
// a perfectly valid one for not being an Irish routing key.
const irishAddressRefine = (data, ctx) => {
  if (!isIrishCountry(data.country)) return;

  if (!data.county?.trim()) {
    ctx.addIssue({
      path: ["county"],
      code: z.ZodIssueCode.custom,
      message: "County is required",
    });
  }

  if (data.postCode && !eircodeRefinement.refine(data.postCode)) {
    ctx.addIssue({
      path: ["postCode"],
      code: z.ZodIssueCode.custom,
      message: eircodeRefinement.refineError,
    });
  }
};

const patientProfileSuperRefine = (data, ctx) => {
  nextOfKinPhoneRefine(data, ctx);
  irishAddressRefine(data, ctx);
  deathFieldsRefine(data, ctx);
  ppsnRefine(data, ctx);
  financialClassRefine(data, ctx);
  refineContactOrEmail({
    contactField: "primaryGpContactId",
    emailField: "primaryGpEmail",
  })(data, ctx);
};

/**
 * @param {"create" | "edit"} mode
 */
export const getPatientProfileFormSchema = (mode) => {
  const sharedDemographicsAndAddress = {
    ...baseShape,
    ethnicityCode: z.string().optional(),
    addressLine1: addressLine1Required,
    county: z.string().optional(),
    postCode: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
    dateOfBirth: dateOfBirthOptional,
    gender: genderRequired,
    mobilePhone: mobilePhoneRequired,
  };

  if (mode === "create") {
    return z
      .object({
        ...sharedDemographicsAndAddress,
        homePhone: optionalE164Phone(),
        email: optionalEmail("Please enter a valid email address"),
        nationalNumber: z.string().optional(),
        gms: z.boolean().optional(),
      })
      .superRefine(patientProfileSuperRefine);
  }

  return z
    .object({
      ...sharedDemographicsAndAddress,
      homePhone: optionalE164Phone(),
      email: optionalEmail("Please enter a valid email address"),
      nationalNumber: z
        .string()
        .optional()
        .refine(ppsnRefinement.refine, ppsnRefinement.refineError),
      gms: z
        .union([z.boolean(), z.string()])
        .optional()
        .transform((val) => val === true || val === "true"),
    })
    .superRefine(patientProfileSuperRefine);
};
